import React, { useEffect, useState, useRef, useCallback } from 'react'
// import SyntaxHighlighter from 'react-syntax-highlighter'
import { Modal, Empty, Button } from 'antd'
import { FullscreenOutlined } from '@ant-design/icons'
import ProductItem from '../../components/search/ProductItem'
import useFeatureMapping, { getNamedFeatures } from '../../components/search/useFeatureMapping'
import { Loading } from '../../components'
import { useSearchContext } from '@pages/discovery/context'
import { getExplainRewrite, getProductCategory } from '../../services/productService'
import { getBrandDetail } from '@services/brandService'
import { getMerchantDetail } from '@services/merchantService'
import ReactJson from 'react-json-view'
import axios from 'axios'
import bxlUtils from 'bxl-utils'
import './productList.scss'

const getFieldByPath = (object: any, path, defaultValue = undefined) => {
    const _path = Array.isArray(path) ? path : path.split('.')
    if (object && _path.length) {
        const next = _path.shift()
        return getFieldByPath(object[next], _path, defaultValue)
    }
    return object === undefined ? defaultValue : object
}

function QueryResultExplain(props) {
    const { queryExplainResult } = props || {}
    const [visible, setVisible] = useState(false)
    if (!queryExplainResult) return null
    const renderFieldLevel = (name, data) => {
        const value = getFieldByPath(data, name)
        if (value === undefined || value === null) return null
        const isSimple = v => typeof v !== 'object'
        return (
            <div>
                <dt>{name}:</dt>
                <dd>
                    <span>{isSimple(value) ? value.toString() : JSON.stringify(value)}</span>
                </dd>
            </div>
        )
    }
    const renderFilterValues = (name, values, meta) => {
        let linkPrefix = ''
        if (name == 'categoryIds') {
            linkPrefix = 'https://5thave-prod.bybieyang.com/api/v1/productcategories/'
        } else if (name == 'brandId') {
            linkPrefix = 'https://5thave-prod.bybieyang.com/api/v1/brands/'
        } else if (name == 'merchantId') {
            linkPrefix = 'https://5thave-prod.bybieyang.com/api/v1/merchants/'
        }
        if (linkPrefix == '') {
            return (Object.keys(values).map((k) => (<dd>{values[k].text}</dd>)))
        } else {
            return (Object.keys(values).map((k) => (<dd><a href={linkPrefix + values[k].text}>{meta[values[k].text] ? meta[values[k].text] : values[k].text}</a></dd>)))
        }
    }
    const renderFilter = (name, data) => {
        const value = getFieldByPath(data, name)
        if (value === undefined || value === null) return null
        const items = Object.keys(value).map((key) => {
            return (<dd><span>
                    {value[key]['name']}
                    {value[key]['negate']? '!=' : '='}
                    {renderFilterValues(value[key]['name'], value[key]['values'], data.meta || {})}
                    </span></dd>)
        })
        return (
            <div>
              <dt>{name}</dt>
              {items}
            </div>
       )
    }
    return (
        <div className='qr-container'>
            <dl>
                {renderFieldLevel('original.query', queryExplainResult)}
                {renderFieldLevel('rewritten.query', queryExplainResult)}
                {renderFieldLevel('fixUpMethod', queryExplainResult)}
                {renderFilter('rewritten.inferredFilters', queryExplainResult)}
            </dl>
            <div className="qr-btn-expand">
                <FullscreenOutlined onClick={() => setVisible(true)} />
            </div>
            <Modal
                width={800}
                visible={visible}
                onCancel={() => setVisible(false)}
                footer={null}
            >
            <ReactJson src={queryExplainResult} quotesOnKeys={false} displayDataTypes={false} enableClipboard={false} name={null} />
            </Modal>
        </div>
    )
}

const ProductsList = () => {
    const { expandFlag, queryParams, searchResult, searchLoading, setQueryParams } = useSearchContext()
    const abortExplainResultHandler = useRef<any>(null)
    const [queryExplainResult, setQureyExplainResult] = useState<any>(null)
    const debounceGetExplainRewrite = useCallback(bxlUtils.debounce(500, (queryParams) => {
        if (abortExplainResultHandler.current) {
            // abort the previous request first
            abortExplainResultHandler.current?.cancel()
        }
        abortExplainResultHandler.current = axios.CancelToken.source()
        getExplainRewrite(queryParams.n, abortExplainResultHandler.current).then(res => {
            if (res.data) {
                if (res.data.rewritten && res.data.rewritten.inferredFilters) {
                    const promises = res.data.rewritten.inferredFilters.map(filter =>
                        filter.values.map(v => {
                            if (filter.name == 'brandId') {
                                return [getBrandDetail(v.text).catch(e => e), filter.name]
                            } else if (filter.name == 'merchantId') {
                                return [getMerchantDetail(v.text).catch(e => e), filter.name]
                            } else if (filter.name == 'categoryIds') {
                                return [getProductCategory(v.text).catch(e => e), filter.name]
                            } else {
                                return null
                            }
                        })
                    ).flat()
                    Promise.all(promises.filter(e => e != null).map(e => e[0])).then(meta => {
                        let resolved = meta.reduce((d: { [key: string]: string; }, o, i) => {
                            if (o && o['data']) {
                                if (promises[i][1] == 'brandId' || promises[i][1] == 'merchantId') {
                                    d[o['data']['id']] = o['data']['name']
                                } else if (promises[i][1] == 'categoryIds') {
                                    d[o['data']['id']] = `${o['data']['displayTerm']}(${o['data']['id']})`
                                }
                            }
                            return d
                        }, {})
                        setQureyExplainResult({
                            ...res.data,
                            meta: resolved
                        })
                    }).catch(e => {})
                }
                setQureyExplainResult(res.data)
            }
        }).catch(e => {
            console.error(e)
        })
    }), [])

    useEffect(() => {
        if (!expandFlag || !queryParams?.n) {
            setQureyExplainResult(null)
            abortExplainResultHandler.current?.cancel()
            return
        }
        debounceGetExplainRewrite(queryParams)
        return () => {
            abortExplainResultHandler.current?.cancel()
        }
    }, [expandFlag, queryParams, debounceGetExplainRewrite])

    const featureMapping = useFeatureMapping(searchResult?.products, expandFlag)
    const renderProductItem = useCallback((products, featureMapping) => {
        if (!products) {
            return null
        }
        return Object.keys(products).map((key) => {
            const product = products[key]
            const attributes = getNamedFeatures(product, featureMapping)
            return <ProductItem product={product} key={product.id} expand={expandFlag} attributes={attributes} />
        })
    }, [expandFlag])

    return (
        <div>
            <QueryResultExplain queryExplainResult={queryExplainResult} />
            <div className="search-product-list">
                {
                    !searchResult?.hasMore && searchResult?.total <= 0
                        ? (
                            <Empty>
                                <Button
                                    onClick={() => {
                                        setQueryParams({ ...queryParams })
                                    }}
                                >
                                    refresh
                                </Button>
                            </Empty>
                        )
                        : (
                            <div style={{ width: '100%', display:'flex', justifyContent: 'flex-start', flexWrap: 'wrap'}}>
                                {renderProductItem(searchResult?.products, featureMapping)}
                            </div>
                        )
                }
            </div>
            {
                searchLoading && <Loading fullScreen spinning className={'bg-loading'} />
            }
        </div>
    )
}

export default ProductsList
